import { apiService } from '@/services/api/apiService';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Type for KYC Document
interface CookKycDocument {
    reviewComment: any;
    statusName: string;
    cookLastName: string;
    cookFirstName: string;
    submittedAt: any;
    documents: any;
    lastName: string;
    isVerified: any;
    documentUrl?: any;
    cookProfileId: any;
    documentTypeId: any;
    uploadedAt: string;
    verifiedAt: any;
    verificationComment: any;
    firstName: string;
    areaCodeName: string;
    code: string;
    CookKycDocumentName: string;
    id: any;
}

// API response type
interface ApiResponse {
    data: CookKycDocument[];
}

// Slice State
interface CooksState {
    data: CookKycDocument[];
    workFlowData: CookKycDocument[];
    loading: boolean;
    error: string | null;
}

const KYCPORT = import.meta.env.VITE_KYC_PORT;
const KYCURL = import.meta.env.VITE_API_KYC_URL;

// ✅ Fetch Cook KYC Documents
export const fetchCookKycDocument = createAsyncThunk<
    CookKycDocument[],
    void,
    { rejectValue: string }
>('CookKycDocument/fetchCookKycDocument', async (_, { rejectWithValue }) => {
    try {
        const response = await apiService.get<ApiResponse>(
            '/get_all_cook_kyc_document',
            KYCPORT,
            KYCURL
        );
        return response.data;
    } catch (error) {
        return rejectWithValue(
            error instanceof Error ? error.message : 'An error occurred'
        );
    }
});

// ✅ Fetch Cook KYC Document Workflow
export const fetchCookKycDocumentWorkFlow = createAsyncThunk<
    CookKycDocument[],
    void,
    { rejectValue: string }
>('CookKycDocument/fetchCookKycDocumentWorkFlow', async (_, { rejectWithValue }) => {
    try {
        const response = await apiService.get<ApiResponse>(
            '/get_all_cook_kyc_workflow',
            KYCPORT,
            KYCURL
        );
        return response.data;
    } catch (error) {
        return rejectWithValue(
            error instanceof Error ? error.message : 'An error occurred'
        );
    }
});
export const updateCookKycWorkflow = createAsyncThunk<CooksState, any, { rejectValue: string }>(
    'CookKycDocument/updateCookKycWorkflow',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.put<{ data: CooksState }>('update_cook_kyc_workflow', params, KYCPORT, KYCURL);
            return response.data;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
        }
    }
);
// ✅ Slice
export const CookKycDocumentSlice = createSlice({
    name: 'CookKycDocument',
    initialState: {
        data: [],
        workFlowData: [],
        loading: false,
        error: null,
    } as CooksState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Handle fetchCookKycDocument
            .addCase(fetchCookKycDocument.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCookKycDocument.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchCookKycDocument.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    action.payload ?? action.error.message ?? 'Failed to fetch cook KYC documents';
            })

            // Handle fetchCookKycDocumentWorkFlow
            .addCase(fetchCookKycDocumentWorkFlow.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCookKycDocumentWorkFlow.fulfilled, (state, action) => {
                state.loading = false;
                state.workFlowData = action.payload;
            })
            .addCase(fetchCookKycDocumentWorkFlow.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    action.payload ?? action.error.message ?? 'Failed to fetch workflow data';
            });
    },
});

export default CookKycDocumentSlice.reducer;
